import {useMemo, useReducer, useRef, useState} from 'react';

import type {BusinessFeeNegotiation} from '@afriex/core/domain/types';
import {
  BUSINESS_FEE_NEGOTIATION_MAX_ROUNDS,
  BusinessFeeNegotiationFilter,
} from '@afriex/core/domain/types';
import {diffNegotiationRounds} from '@afriex/core/utils';

import type {FeeNegotiationAnswer} from '@/services/merchants';
import {merchantService} from '@/services/merchants';
import {
  getResponseMessage,
  getThrownMessage,
  readErrorField,
} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';
import {FeeNegotiationCounterValidator} from '@/validators/FeeNegotiationCounterValidator';

import negotiationAnswerReducer, {
  initialNegotiationAnswerState,
} from './negotiationAnswerReducer';
import {NEGOTIATION_FILTER_LABELS} from './negotiationFormat';

const under = (filter: BusinessFeeNegotiationFilter) =>
  `It is now under ${NEGOTIATION_FILTER_LABELS[filter]}.`;

/** The toast after each answer, saying where the request went. */
const SUCCESS_MESSAGES: Record<FeeNegotiationAnswer['action'], string> = {
  accept: `Pricing accepted. ${under(BusinessFeeNegotiationFilter.Completed)}`,
  reject: `Pricing request declined. ${under(BusinessFeeNegotiationFilter.Completed)}`,
  counter: `Counter sent to the merchant. ${under(BusinessFeeNegotiationFilter.AwaitingBusiness)}`,
  apply: 'Pricing applied',
};

const getAnswerErrorMessage = (error: unknown): string =>
  getResponseMessage(error) ||
  readErrorField(error, 'response', 'data', 'error') ||
  getThrownMessage(error) ||
  'Could not send that answer. Try again.';

/**
 * Afriex's answer to one negotiation: the counter form, the confirm step in
 * front of each action, and the call itself. The review keys it by
 * negotiation, so a different request starts from a clean form.
 */
export const useNegotiationAnswer = (
  negotiation: BusinessFeeNegotiation,
  onAnswered: () => void,
) => {
  const [state, dispatch] = useReducer(
    negotiationAnswerReducer,
    initialNegotiationAnswerState,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set before the call goes out, so a second click in the same tick, before
  // `isSubmitting` has rendered, cannot send the answer twice.
  const isInFlight = useRef(false);

  const lines = useMemo(
    () => diffNegotiationRounds(negotiation.rounds ?? []),
    [negotiation.rounds],
  );

  const roundCount = negotiation.rounds?.length ?? 0;
  const isFinalRound = roundCount >= BUSINESS_FEE_NEGOTIATION_MAX_ROUNDS;
  const validator = useMemo(
    () =>
      new FeeNegotiationCounterValidator(
        lines,
        state.texts,
        state.counterNote,
        roundCount,
      ),
    [lines, state.texts, state.counterNote, roundCount],
  );

  const send = async (payload: FeeNegotiationAnswer) => {
    if (!negotiation.id || isInFlight.current) {
      return;
    }
    isInFlight.current = true;
    setIsSubmitting(true);
    setError(null);
    try {
      await merchantService.respondToFeeNegotiation(
        negotiation.businessId,
        negotiation.id,
        payload,
      );
      showSuccessMessage(SUCCESS_MESSAGES[payload.action]);
      dispatch({type: 'RESET'});
      onAnswered();
    } catch (err) {
      const message = getAnswerErrorMessage(err);
      setError(message);
      showErrorMessage(message);
    } finally {
      isInFlight.current = false;
      setIsSubmitting(false);
    }
  };

  /** Moves to the confirm step for a counter, or says why it cannot go. */
  const askToSendCounter = () => {
    try {
      validator.value();
      setError(null);
      dispatch({type: 'ASK_CONFIRM', confirm: 'counter'});
    } catch (err) {
      setError(getThrownMessage(err) ?? 'Check the prices and try again.');
    }
  };

  const rejectionNote = state.rejectionNote.trim();

  /** Runs whichever action the open confirm modal is standing in front of. */
  const confirm = async () => {
    if (state.confirm === 'accept') {
      await send({action: 'accept'});
    } else if (state.confirm === 'decline') {
      // The server requires the reason too; this keeps the call from going out.
      if (rejectionNote) {
        await send({action: 'reject', rejectionNote});
      }
    } else if (state.confirm === 'apply') {
      await send({action: 'apply'});
    } else if (state.confirm === 'counter') {
      try {
        await send({action: 'counter', ...validator.value()});
      } catch (err) {
        setError(getThrownMessage(err) ?? 'Check the prices and try again.');
      }
    }
  };

  return {
    state,
    dispatch,
    lines,
    validator,
    isSubmitting,
    isFinalRound,
    canDecline: rejectionNote.length > 0,
    error,
    setError,
    askToSendCounter,
    confirm,
  };
};

export type NegotiationAnswer = ReturnType<typeof useNegotiationAnswer>;
